import { Configuration } from './generated';
import globalAxios, { AxiosInstance } from 'axios';
import { ProxyAPI, ProxyApiType } from './proxy';
import { OSKFeature, OSKGeoJson } from '../models';

export type ProductCollection = Array<{
    id: string;
    description: string;
    keywords: string[];
}>;

export type TaskingOrderRequest = {
    order_parameters: {
        name: string;
        description?: string;
    };
    datetime: string;
    geometry: {
        type: string;
        coordinates: number[];
    };
};

export type TaskingOrderStatus = {
    reason_code: string;
    reason_text: string;
    status_code: string;
    timestamp: string;
};

export enum TaskingOrderStatuses {
    received,
    accepted,
    active,
    scheduled,
    tasked,
    processing,
    processed,
    delivered,
    rejected,
    cancelled,
    expired,
    failed,
}

export type TaskingOrder = {
    geometry: OSKFeature;
    id: string;
    properties: {
        created: string; // Date
        opportunity_parameters: {};
        order_parameters: {
            contract_type: string;
            data_format: string;
            description: string;
            imaging_mode: string;
            name: string;
            point_of_contact: string;
            priority: number;
            processing_level: string;
            program_id: number;
            resolution: string;
            user_id: number;
        };
        product_id: string;
        search_parameters: {
            datetime: string; // in './.' format
            filter: unknown;
            geometry: OSKGeoJson;
        };
        status: TaskingOrderStatus;

        stac_item_ids: string[];
        stac_collection: string;
    };
    stapi_type: string;
    stapi_version: string;
    type: string;
};

type TaskingOrdersRequest = {
    /** The index to start returning results from.
     * Usually calculated as `current page * page size`.
     */
    next?: number;

    /** The # of results to return for this query.
     * Usually the page size. */
    limit?: number;
};

type TaskingOrdersResponse = {
    /** The order objects for the request. */
    features: TaskingOrder[];

    /** The total number of orders that were matched.
     * Used for pagination calculations. */
    numberMatched: number;
};

/**
 * OrderApi - object-oriented interface
 * @export
 * @class OrderApi
 * @extends {ProxyAPI}
 */
export class OrdersApi extends ProxyAPI {
    constructor(configuration?: Configuration, protected axios: AxiosInstance = globalAxios) {
        super(ProxyApiType.ORDERS, configuration, axios);
    }

    /** A method that returns all orders available to this user.
     * It's automatically filtered to only return orders that the user
     * has access to.
     */
    public getOrders(params?: TaskingOrdersRequest) {
        return this.axios
            .get(`${this.basePath}/orders`, {
                params: {
                    ...params,
                },
            })
            .then((response) => response.data as TaskingOrdersResponse);
    }

    /** A method that returns all orders available to this user.
     * It's automatically filtered to only return products that the user
     * has access to.
     */
    public getProducts() {
        return this.axios
            .get(`${this.basePath}/products`, {})
            .then((response) => response.data.products as ProductCollection);
    }

    public updateOrderStatus(id: string, status: string, user: string) {
        return this.axios
            .post(`${this.basePath}/orders/${id}/statuses`, {
                osk_status_code: status,
                reason_code: 'REASON0',
                reason_text: `Set by ${user}`,
            })
            .then((response) => response.data);
    }

    /** A method that creates an order with the provided `order` object. */
    public createOrder(product: string, order: TaskingOrderRequest) {
        return this.axios.post(`${this.basePath}/products/${product}/orders`, order).then((response) => response.data);
    }
}
